import { useState, useMemo } from 'react';
import { currencyList, currencySymbols, type CurrencyCode, bankList } from '@/mocks/cash';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    amount: number;
    description: string;
    currency: CurrencyCode;
    method: string;
    bank?: string;
    notes: string;
  }) => void;
  customerName: string;
}

const paymentMethods = [
  'Nakit',
  'Kredi Kartı',
  'Havale',
  'EFT',
  'Açık Ödeme',
  'Eksik Ödeme',
];

export default function PaymentModal({ open, onClose, onSave, customerName }: PaymentModalProps) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('TRY');
  const [method, setMethod] = useState('Nakit');
  const [bank, setBank] = useState('');
  const [notes, setNotes] = useState('');

  const todayStr = '2026-05-09';

  const reset = () => {
    setAmount('');
    setDescription('');
    setCurrency('TRY');
    setMethod('Nakit');
    setBank('');
    setNotes('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = () => {
    const numAmount = Number(amount);
    if (!amount || numAmount <= 0) {
      alert('Lütfen geçerli bir tutar girin.');
      return;
    }
    if (!description.trim()) {
      alert('Lütfen açıklama girin.');
      return;
    }
    onSave({
      amount: numAmount,
      description: description.trim(),
      currency,
      method,
      bank: ['Havale', 'EFT', 'Kredi Kartı'].includes(method) ? bank || undefined : undefined,
      notes: notes.trim(),
    });
    reset();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={handleClose}>
      <div className="spa-card w-full max-w-md flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div>
            <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>Tahsilat Yap</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{customerName}</p>
          </div>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-close-line" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Tutar <span style={{ color: '#EF4444' }}>*</span></label>
              <input
                type="number"
                className="spa-input text-sm"
                placeholder="0"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Para Birimi</label>
              <select
                className="spa-input text-sm"
                value={currency}
                onChange={e => setCurrency(e.target.value as CurrencyCode)}
              >
                {currencyList.map(c => (
                  <option key={c} value={c}>{c} ({currencySymbols[c]})</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Açıklama <span style={{ color: '#EF4444' }}>*</span></label>
            <input
              className="spa-input text-sm"
              placeholder="Örn. Klasik Masaj - 90 dk"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Ödeme Yöntemi</label>
              <select
                className="spa-input text-sm"
                value={method}
                onChange={e => setMethod(e.target.value)}
              >
                {paymentMethods.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            {['Havale', 'EFT', 'Kredi Kartı'].includes(method) && (
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Banka</label>
                <select
                  className="spa-input text-sm"
                  value={bank}
                  onChange={e => setBank(e.target.value)}
                >
                  <option value="">Banka Seçin</option>
                  {bankList.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Notlar</label>
            <textarea
              className="spa-input text-sm min-h-[60px] resize-none"
              placeholder="Opsiyonel notlar..."
              maxLength={500}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
            <p className="text-[10px] mt-1 text-right" style={{ color: 'var(--text-muted)' }}>{notes.length}/500</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <button onClick={handleClose} className="spa-btn-outline text-sm">İptal</button>
          <button onClick={handleSave} className="spa-btn-primary text-sm flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-xs" /></div>
            Tahsilat Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}